// Krapf Charter Mail — the operator's charter inquiry inbox.
//
// Outlook is the front door. A charter inquiry arrives; the operator clicks "This is a trip inquiry";
// that starts a Movorra Studio (ECAP) flow session seeded with the email body. The flow extracts what
// it can and reports what is still missing; this page composes ONE reply email asking for all of it,
// the operator sends it, and when the customer replies the poll feeds it back in as the next turn.
// When the flow reports COMPLETE, a human clicks "Send for pricing".
//
// The human-in-the-loop trigger is deliberate, not a limitation: a real charter mailbox is mostly
// everything else, and nothing here tries to guess which mail is charter business.
//
// Two channels, never merged (CLAUDE.md): the draft email carries only what the customer needs to
// answer; warnings, a volunteered round trip, and anything the intake gave up on go to the reviewer
// panel and never into the customer's inbox.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, DemoBadge } from '../ui/primitives';
import {
  IconRefresh, IconCircleCheck, IconWarning, IconUser, IconCalendar, IconClock,
  IconUsers, IconPin, IconBuilding, IconSparkles, IconLock, IconX,
} from '../ui/icons';
import { cx } from '../ui/cx';
import { mail, harness, isMock } from '../services/mailProvider';
import {
  beginIntake, continueIntake, sessionForThread, isWatched, noteLastMessage, forgetThread,
  cleanValues, missingItems, optionalGaps, reviewFlags, changedKeys,
  threadStatus, THREAD_STATUS, turnWasUnproductive, composeReply,
  saveThreadState, threadState, setTripKind, isReady,
  highlightExtracted, replyLines,
} from '../services/krapfIntake';

import { accountForSender, accountPrefill } from '../applications/config/krapfAccounts';
import { FIELD_BY_KEY } from '../services/krapfIntakeFields';
import { stripQuotedAndSignature, htmlToText } from '../services/emailText';

const POLL_MS = 20000;

const STATUS_TONE = {
  [THREAD_STATUS.READY]: 'bg-success-50 text-success',
  [THREAD_STATUS.WAITING]: 'bg-accent/15 text-krapf-navy',
  [THREAD_STATUS.NEEDS_REPLY]: 'bg-info-50 text-info',
  [THREAD_STATUS.STALLED]: 'bg-danger-50 text-danger',
  [THREAD_STATUS.NOT_TRACKED]: 'bg-border/50 text-body/60',
};

// The trip sheet reads in itinerary order, which is not the flow's declaration order.
/** The return leg — only meaningful on a round trip. */
const RETURN_KEYS = new Set(['returnDate', 'returnTime']);

const SHEET_ROWS = [
  { key: 'groupName', label: 'Group', icon: <IconBuilding size={14} /> },
  { key: 'contactName', label: 'Contact', icon: <IconUser size={14} /> },
  { key: 'contactPhone', label: 'Phone' },
  { key: 'contactEmail', label: 'Email' },
  { key: 'departureDate', label: 'Date', icon: <IconCalendar size={14} /> },
  { key: 'departureTime', label: 'Departs', icon: <IconClock size={14} /> },
  { key: 'pickupLocation', label: 'Pickup', icon: <IconPin size={14} /> },
  { key: 'pickupCity', label: 'Pickup city' },
  { key: 'pickupState', label: 'Pickup state' },
  { key: 'dropoffLocation', label: 'Destination', icon: <IconPin size={14} /> },
  { key: 'dropoffCity', label: 'Destination city' },
  { key: 'dropoffState', label: 'Destination state' },
  { key: 'returnDate', label: 'Return date', icon: <IconCalendar size={14} /> },
  { key: 'returnTime', label: 'Returns', icon: <IconClock size={14} /> },
  { key: 'arriveByTime', label: 'Arrive by' },
  { key: 'passengers', label: 'Passengers', icon: <IconUsers size={14} /> },
  { key: 'luggageEquipment', label: 'Luggage / equipment' },
  { key: 'coachType', label: 'Coach' },
  { key: 'billingAccount', label: 'Billing' },
  { key: 'poNumber', label: 'PO number' },
  { key: 'dayOfContact', label: 'Day-of contact' },
  { key: 'specialRequests', label: 'Special requests' },
];

function Chip({ tone = 'bg-border/50 text-body/60', children }) {
  return <span className={cx('inline-block text-xs font-medium px-2 py-0.5 rounded', tone)}>{children}</span>;
}

/** Where a value came from. "From email"/"Looked up" are real; the account sources are seeded demo data. */
function SourceBadge({ source }) {
  if (!source) return null;
  const demo = source === 'Account' || source === 'Past trips' || source === 'Account rule';
  return (
    <span className="inline-flex items-center gap-1">
      <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-border/60 text-body/60">{source}</span>
      {demo && <DemoBadge className="scale-90 origin-left" />}
    </span>
  );
}

const TRIP_KINDS = [
  { value: 'one-way', label: 'One-way' },
  { value: 'round-trip', label: 'Round trip' },
];

/**
 * One-way / round trip, pre-selected from whatever the intake made of the email and changeable at
 * any time. The operator is never asked to decide BEFORE the intake runs: the email almost always
 * says, so the useful moment to involve a human is correcting a wrong call, not pre-empting one.
 */
function TripKindPicker({ value, onChange, disabled }) {
  return (
    <div className="inline-flex rounded-btn border border-krapf-border overflow-hidden" role="group">
      {TRIP_KINDS.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={String(o.value)}
            type="button"
            disabled={disabled}
            aria-pressed={active}
            onClick={() => onChange(o.value)}
            className={cx(
              'px-2.5 py-1 text-[12px] whitespace-nowrap transition-colors disabled:opacity-50',
              active ? 'bg-krapf-navy text-white' : 'bg-white text-body/70 hover:bg-krapf-surface',
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Outlook stamps today's mail with a time and everything older with a date. */
function mailStamp(iso) {
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleDateString([], { month: 'numeric', day: 'numeric' });
}

// Outlook gives each correspondent a coloured initials disc. Hashing the name keeps a given sender
// the same colour on every render without storing anything.
const AVATAR_TONES = [
  'bg-krapf-navy text-white', 'bg-krapf-gold text-krapf-navy', 'bg-info text-white',
  'bg-success text-white', 'bg-danger text-white', 'bg-krapf-navy-dark text-white',
];

function Avatar({ name }) {
  const label = String(name || '?').trim();
  const initials = label.split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';
  let hash = 0;
  for (let i = 0; i < label.length; i += 1) hash = (hash * 31 + label.charCodeAt(i)) >>> 0;
  return (
    <span className={cx('w-8 h-8 rounded-full shrink-0 inline-flex items-center justify-center text-[11px] font-semibold',
      AVATAR_TONES[hash % AVATAR_TONES.length])}>
      {initials}
    </span>
  );
}

/**
 * An email body with the words the intake actually read underlined.
 *
 * Only ever applied to an INBOUND message: highlighting our own reply would be marking up our own
 * words. See highlightExtracted for why a computed value (a totalled headcount, an ISO date) does
 * not light up — it was never in the text to underline.
 */
function HighlightedBody({ text, turn }) {
  const parts = useMemo(() => highlightExtracted(text, turn), [text, turn]);
  return (
    <pre className="text-[13px] text-body/80 whitespace-pre-wrap font-sans">
      {parts.map((p, i) => (p.key ? (
        <mark
          key={i}
          title={`Read as ${(FIELD_BY_KEY[p.key] && FIELD_BY_KEY[p.key].label) || p.key}`}
          className="bg-krapf-gold-tint text-body underline decoration-krapf-gold decoration-2 underline-offset-2 rounded-sm px-0.5"
        >
          {p.text}
        </mark>
      ) : <span key={i}>{p.text}</span>))}
    </pre>
  );
}

/** Our own reply, with the numbered asks weighted — they are the only part needing an answer. */
function ReplyBody({ text }) {
  return (
    <pre className="text-[13px] text-body/80 whitespace-pre-wrap font-sans">
      {replyLines(text).map((l, i) => (
        <span key={i} className={cx('block', l.ask && 'font-semibold text-krapf-navy')}>
          {l.text || '\u00a0'}
        </span>
      ))}
    </pre>
  );
}

function bodyText(message) {
  if (!message) return '';
  const raw = message.bodyIsHtml ? htmlToText(message.body) : message.body;
  return stripQuotedAndSignature(raw) || raw;
}

export default function KrapfCharterMail({ cfg, userName, onLogout, anonymous = false }) {
  const flowKey = (cfg && cfg.flowKey) || '';
  const clientId = (process.env.REACT_APP_KRAPF_GRAPH_CLIENT_ID || '').trim();

  const [account, setAccount] = useState(null);
  const [threads, setThreads] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [turn, setTurn] = useState(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [awaitingCustomer, setAwaitingCustomer] = useState(false);
  const prevMissing = useRef([]);

  // In mock mode there is no Azure app registration to configure; only the flow key matters,
  // because the agent itself is real either way.
  const configured = (isMock() || !!clientId) && !!flowKey;

  useEffect(() => { if (clientId) mail().configure({ clientId }); }, [clientId]);
  useEffect(() => { mail().currentAccount().then(setAccount).catch(() => {}); }, []);

  const refreshThreads = useCallback(async () => {
    if (!account) return;
    try {
      setThreads(await mail().listThreads({ top: 40 }));
      setError('');
    } catch (e) { setError(e.message); }
  }, [account]);

  useEffect(() => { refreshThreads(); }, [refreshThreads]);

  const selected = useMemo(
    () => threads.find((t) => t.conversationId === selectedId) || null,
    [threads, selectedId],
  );

  // Re-open a thread exactly as it was left. The intake lives on the SERVER (one session per thread);
  // losing the panel on a thread switch was only ever a client-side amnesia, so restore from the same
  // record that already remembers the session link.
  const openThread = useCallback(async (conversationId) => {
    const saved = threadState(conversationId);
    setSelectedId(conversationId);
    setTurn((saved && saved.turn) || null);
    setDraft((saved && saved.draft) || '');
    setAwaitingCustomer(!!(saved && saved.awaitingCustomer));
    // The unproductive-turn check compares against the gap list we last showed, so it has to come back too.
    prevMissing.current = (saved && saved.turn && saved.turn.missingFields) || [];
    setNotice('');
    try {
      setMessages(await mail().getThread(conversationId));
    } catch (e) { setError(e.message); }
  }, []);

  // Persist whatever the open thread is currently showing. Keystroke-rate, but the record is a few KB
  // and this is what makes a hand-edited draft survive a thread switch too. A no-op for a thread that
  // was never marked as an inquiry.
  useEffect(() => {
    if (!selectedId) return;
    saveThreadState(selectedId, { turn, draft, awaitingCustomer });
  }, [selectedId, turn, draft, awaitingCustomer]);

  // Mark a thread as a trip inquiry — the human-in-the-loop trigger.
  const markAsInquiry = useCallback(async () => {
    if (!selected) return;
    setBusy('Reading the inquiry…');
    setError('');
    try {
      const opening = messages[0] || selected.latest;
      const { turn: t } = await beginIntake(flowKey, opening);
      setTurn(t);
      prevMissing.current = t.missingFields || [];
      setAwaitingCustomer(false);
      setDraft(composeReply(t, {
        customerName: opening.fromName,
        senderName: userName || '',
      }));
    } catch (e) { setError(e.message); } finally { setBusy(''); }
  }, [selected, messages, flowKey, userName]);

  // The operator correcting the intake's call. No email and no LLM turn: component-result writes
  // the value and resumes, so the return questions appear in (or vanish from) the numbered list
  // immediately and the draft is recomposed from the fresh TurnResult.
  const changeTripKind = useCallback(async (kind) => {
    const link = selected && sessionForThread(selected.conversationId);
    if (!link || !kind) return;
    setBusy('Updating the trip…');
    setError('');
    try {
      const t = await setTripKind(link.sessionId, kind);
      setTurn(t);
      prevMissing.current = t.missingFields || [];
      setDraft(composeReply(t, {
        customerName: (selected.latest && selected.latest.fromName) || '',
        senderName: userName || '',
      }));
    } catch (e) { setError(e.message); } finally { setBusy(''); }
  }, [selected, userName]);

  // A new inquiry from the rail: land it in the inbox, refresh the list, and open it — the next thing
  // you want is always to read it and decide whether it is a trip inquiry.
  const deliverInquiry = useCallback(async (conversationId) => {
    await refreshThreads();
    await openThread(conversationId);   // clears `notice`, so set it after
    setNotice('New inquiry delivered to the inbox.');
  }, [refreshThreads, openThread]);

  // A thread can be WATCHED but have no cached turn: a record written before the panel cached its
  // TurnResult, or site data partly cleared. The panel cannot be rebuilt from the server — ResumeInfo's
  // missing list ignores requiredIf/excused/suppressed/derived — so the honest recovery is a fresh
  // session over the same thread, not a reconstructed half-truth.
  const restartIntake = useCallback(async () => {
    if (!selected) return;
    forgetThread(selected.conversationId);
    await markAsInquiry();
  }, [selected, markAsInquiry]);

  // Send the drafted reply as a real Outlook reply, then wait on the customer.
  const sendDraft = useCallback(async () => {
    if (!selected || !draft.trim()) return;
    setBusy('Sending…');
    try {
      const last = messages[messages.length - 1] || selected.latest;
      const draftId = await mail().createReplyDraft(last.id, draft);
      await mail().sendDraft(draftId);
      setAwaitingCustomer(true);
      setDraft('');
      // Write through before re-reading the thread: the state setters above have not flushed yet, so a
      // round trip through openThread would restore the pre-send draft. Refresh the messages in place
      // instead of re-opening — re-opening would also clear the notice we are about to set.
      saveThreadState(selected.conversationId, { draft: '', awaitingCustomer: true });
      setMessages(await mail().getThread(selected.conversationId));
      setNotice('Reply sent. Watching this thread for their answer.');
    } catch (e) { setError(e.message); } finally { setBusy(''); }
  }, [selected, draft, messages]);

  // Poll watched threads for a customer reply and feed it in as the next turn.
  useEffect(() => {
    if (!account || !selected || !isWatched(selected.conversationId)) return undefined;
    let alive = true;
    const tick = async () => {
      try {
        const fresh = await mail().getThread(selected.conversationId);
        if (!alive || !fresh.length) return;
        const link = sessionForThread(selected.conversationId);
        const newest = fresh[fresh.length - 1];
        // Only a message we have not fed in, and only one FROM the customer (not our own reply).
        const isOurs = account && newest.fromAddress
          && newest.fromAddress.toLowerCase() === String(account.username || '').toLowerCase();
        if (!link || newest.id === link.lastMessageId || isOurs) {
          if (newest.id !== link?.lastMessageId && isOurs) noteLastMessage(selected.conversationId, newest.id);
          return;
        }
        setMessages(fresh);
        setBusy('Reading their reply…');
        const before = prevMissing.current;
        const t = await continueIntake(link.sessionId, newest);
        if (!alive) return;
        setTurn(t);
        prevMissing.current = t.missingFields || [];
        setAwaitingCustomer(false);
        if (turnWasUnproductive(t, before)) {
          // Re-sending the same questions at someone who just asked US something reads as a bot.
          setNotice('Their reply did not answer any of the open questions — worth a human reading it before replying.');
          setDraft('');
        } else {
          setDraft(composeReply(t, { customerName: newest.fromName, senderName: userName || '' }));
        }
      } catch (e) { if (alive) setError(e.message); } finally { if (alive) setBusy(''); }
    };
    const h = setInterval(tick, POLL_MS);
    return () => { alive = false; clearInterval(h); };
  }, [account, selected, userName]);

  const values = cleanValues(turn);
  const prefill = accountPrefill(accountForSender(selected && selected.latest.fromAddress));
  const gaps = missingItems(turn);
  const soft = optionalGaps(turn);
  const flags = reviewFlags(turn);
  const changed = new Set(changedKeys(turn));
  const status = threadStatus(turn, { awaitingCustomer });
  const ready = isReady(turn);
  // A suppressed field KEEPS whatever it had, so flipping a round trip back to one-way would leave
  // a return date sitting on the sheet. Hide the return leg whenever the trip is not a round trip.
  const roundTrip = values.returnJourney === 'round-trip';
  const sheetRows = SHEET_ROWS.filter((r) => roundTrip || !RETURN_KEYS.has(r.key));

  if (!configured) return <SetupNeeded clientId={clientId} flowKey={flowKey} />;

  if (!account) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-krapf-surface gap-4 px-6 text-center">
        <IconSparkles size={28} />
        <h1 className="text-xl font-semibold text-krapf-navy">Krapf Charter Mail</h1>
        <p className="text-sm text-body/70 max-w-md">
          {isMock()
            ? 'Test mailbox — seeded charter inquiries and ordinary mail, with no Microsoft account needed. The intake agent behind it is the real one.'
            : 'Sign in with the Microsoft account whose mailbox receives charter inquiries. The page reads and replies in that mailbox only.'}
        </p>
        <Button onClick={() => mail().signIn().then(setAccount).catch((e) => setError(e.message))}>
          {isMock() ? 'Open the test mailbox' : 'Sign in with Microsoft'}
        </Button>
        {error && <p className="text-sm text-danger max-w-md">{error}</p>}
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-krapf-surface">
      <header className="flex items-center gap-3 px-5 h-14 bg-krapf-navy shrink-0 text-white">
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-krapf-gold text-krapf-navy font-bold text-[13px] shrink-0">K</span>
        <span className="font-semibold tracking-tight">KRAPF</span>
        <span className="text-white/55 text-sm">Charter Mail</span>
        {isMock() && <DemoBadge title="Test mailbox — the mail is simulated, the intake agent is real." />}
        <div className="ml-auto flex items-center gap-3">
          <span className="text-xs text-white/60">{account.username}</span>
          <button type="button" onClick={refreshThreads} title="Refresh"
                  className="p-1.5 rounded-btn text-white/70 hover:text-white hover:bg-white/10">
            <IconRefresh size={15} />
          </button>
          {!anonymous && (
            <button type="button" onClick={() => mail().signOut().then(() => setAccount(null))}
                    className="text-xs text-white/70 hover:text-white px-2 py-1 rounded-btn hover:bg-white/10">
              Sign out
            </button>
          )}
          {onLogout && (
            <button type="button" onClick={onLogout}
                    className="text-xs text-white/70 hover:text-white px-2 py-1 rounded-btn hover:bg-white/10">
              Exit
            </button>
          )}
        </div>
      </header>

      {(error || busy || notice) && (
        <div className={cx('px-5 py-2 text-sm border-b border-krapf-border',
          error ? 'bg-danger-50 text-danger' : notice ? 'bg-krapf-gold-tint text-krapf-navy' : 'bg-info-50 text-info')}>
          {error || busy || notice}
        </div>
      )}

      <div className="flex-1 flex min-h-0">
        {/* Thread list */}
        <aside className="w-80 shrink-0 bg-white border-r border-krapf-border overflow-y-auto">
          <div className="px-4 py-2 border-b border-krapf-border flex items-center gap-2 sticky top-0 bg-white z-10">
            <span className="text-sm font-semibold text-krapf-navy">Inbox</span>
            <span className="text-[11px] text-body/40">{threads.length}</span>
            <div className="ml-auto">
              <NewInquiryButton onDelivered={deliverInquiry} />
            </div>
          </div>
          {threads.map((t) => {
            const watched = isWatched(t.conversationId);
            const active = selectedId === t.conversationId;
            const who = t.latest.fromName || t.latest.fromAddress;
            return (
              <button
                key={t.conversationId}
                onClick={() => openThread(t.conversationId)}
                className={cx('w-full text-left flex gap-3 px-3 py-2.5 border-b border-krapf-border/60',
                  active ? 'bg-krapf-navy-tint' : 'hover:bg-krapf-surface')}
              >
                {/* Outlook's left rail: a colour bar on the selected row, an initials avatar per sender. */}
                <span className={cx('w-0.5 -my-2.5 rounded-full shrink-0',
                  active ? 'bg-krapf-navy' : 'bg-transparent')} />
                <Avatar name={who} />
                <span className="min-w-0 flex-1">
                  <span className="flex items-baseline justify-between gap-2">
                    <span className="text-[13px] font-semibold text-krapf-navy truncate">{who}</span>
                    <span className="text-[11px] text-body/40 shrink-0">{mailStamp(t.latest.receivedAt)}</span>
                  </span>
                  <span className="block text-[13px] text-body truncate">{t.subject || '(no subject)'}</span>
                  <span className="block text-xs text-body/50 truncate">{t.latest.preview}</span>
                  {watched && (
                    <span className="inline-block mt-1">
                      <Chip tone="bg-krapf-gold-tint text-krapf-gold-dark">Trip intake on</Chip>
                    </span>
                  )}
                </span>
              </button>
            );
          })}
          {!threads.length && <div className="px-4 py-6 text-sm text-body/50">No mail in this inbox.</div>}
        </aside>

        {/* Reading pane */}
        <main className="flex-1 min-w-0 overflow-y-auto">
          {!selected && <div className="p-10 text-sm text-body/50">Select an email to read it.</div>}
          {selected && (
            <div className="p-6">
              <h2 className="text-lg font-semibold text-body">{selected.subject || '(no subject)'}</h2>
              {!isWatched(selected.conversationId) && (
                <div className="mt-4 flex items-center gap-3 rounded-card border border-krapf-gold/50 bg-krapf-gold-tint px-4 py-3">
                  <IconSparkles size={16} />
                  <span className="text-sm text-body/80">Is this a charter trip request?</span>
                  <Button size="sm" className="ml-auto" onClick={markAsInquiry} disabled={!!busy}>
                    This is a trip inquiry
                  </Button>
                </div>
              )}
              {isWatched(selected.conversationId) && !turn && !busy && (
                <div className="mt-4 flex items-center gap-3 rounded-card border border-krapf-border bg-krapf-surface px-4 py-3">
                  <IconWarning size={16} />
                  <span className="text-sm text-body/80">
                    This thread is marked as a trip inquiry, but this browser has no copy of the details.
                  </span>
                  <Button size="sm" variant="outline" className="ml-auto" onClick={restartIntake}>
                    Start the intake again
                  </Button>
                </div>
              )}
              <div className="mt-4 space-y-4">
                {messages.map((m) => (
                  <article key={m.id} className="bg-white border border-krapf-border rounded-card p-4">
                    <div className="flex items-center gap-2.5 mb-2">
                      <Avatar name={m.fromName || m.fromAddress} />
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-krapf-navy truncate">
                          {m.fromName || m.fromAddress}
                        </span>
                        <span className="block text-xs text-body/40">{m.fromAddress}</span>
                      </span>
                      <span className="ml-auto text-xs text-body/40 shrink-0">
                        {new Date(m.receivedAt).toLocaleString()}
                      </span>
                    </div>
                    {m.outgoing
                      ? <ReplyBody text={bodyText(m)} />
                      : <HighlightedBody text={bodyText(m)} turn={turn} />}
                  </article>
                ))}
              </div>

              {turn && (
                <section className="mt-6 bg-white border border-krapf-border rounded-card p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-sm font-semibold text-body">Draft reply</h3>
                    <span className="text-xs text-body/50">You send it — nothing goes out on its own.</span>
                  </div>
                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    rows={12}
                    className="w-full text-[13px] font-mono border border-krapf-border rounded-btn p-3 focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder="Nothing to ask right now."
                  />
                  <div className="mt-2 flex items-center gap-2">
                    <Button onClick={sendDraft} disabled={!draft.trim() || !!busy}>Send reply</Button>
                    <Button variant="outline" onClick={() => setDraft(composeReply(turn, {
                      customerName: selected.latest.fromName, senderName: userName || '',
                    }))}>Reset draft</Button>
                  </div>
                </section>
              )}
            </div>
          )}
          {isMock() && (
            <MockHarness
              selected={selected}
              onChanged={async () => { await refreshThreads(); if (selected) setMessages(await mail().getThread(selected.conversationId)); }}
            />
          )}
        </main>

        {/* Trip intake rail */}
        <aside className="w-96 shrink-0 bg-white border-l border-krapf-border overflow-y-auto">
          <div className="px-4 py-3 border-b border-krapf-border flex items-center gap-2">
            <span className="text-sm font-semibold text-body">Trip intake</span>
            <Chip tone={STATUS_TONE[status]}>{status}</Chip>
          </div>

          {!turn && (
            <p className="px-4 py-6 text-sm text-body/50">
              Mark an email as a trip inquiry and the details it contains will appear here.
            </p>
          )}

          {turn && (
            <>
              {/* Pre-selected from whatever the intake made of the email, and always changeable —
                  a wrong call here silently prices the wrong trip. */}
              <div className="px-4 py-3 border-b border-krapf-border">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-[11px] uppercase tracking-wide text-body/50">Return journey</span>
                  {turn.extracted && turn.extracted.returnJourney && <SourceBadge source="From email" />}
                  {changed.has('returnJourney') && <Chip tone="bg-accent/20 text-krapf-navy">Changed</Chip>}
                </div>
                <TripKindPicker
                  value={values.returnJourney || 'one-way'}
                  onChange={changeTripKind}
                  disabled={!!busy}
                />
                <div className="text-xs text-body/50 mt-1">
                  {values.returnJourney
                    ? 'Picked up from the email — change it if that’s wrong.'
                    : 'The email didn’t say, so it’s being treated as one-way — change it if that’s wrong.'}
                </div>
              </div>
              <div className="px-4 py-3 space-y-3">
                {sheetRows.map(({ key, label, icon }) => {
                  const live = values[key];
                  const known = prefill[key];
                  if (!live && !known) return null;
                  const source = live ? (turn.extracted && turn.extracted[key] ? 'From email' : null) : known.source;
                  return (
                    <div key={key}>
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] uppercase tracking-wide text-body/50">{label}</span>
                        <SourceBadge source={source} />
                        {changed.has(key) && <Chip tone="bg-accent/20 text-krapf-navy">Changed</Chip>}
                      </div>
                      <div className="text-sm text-body flex items-center gap-2">
                        {icon}
                        <span>{live || known.value}</span>
                      </div>
                      {!live && known && known.note && (
                        <div className="text-xs text-body/50">{known.note}</div>
                      )}
                    </div>
                  );
                })}
              </div>

              {!!gaps.length && (
                <div className="px-4 py-3 border-t border-krapf-border">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-semibold text-body">Still needed to quote</span>
                    <Chip tone="bg-accent/20 text-krapf-navy">{gaps.length}</Chip>
                  </div>
                  <ul className="space-y-1.5">
                    {gaps.map((g) => (
                      <li key={g.key} className="text-[13px] text-body/80 flex gap-2">
                        <span className="text-krapf-gold-dark mt-1">•</span><span>{g.label}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {!!soft.length && (
                <div className="px-4 py-3 border-t border-krapf-border">
                  <div className="text-xs uppercase tracking-wide text-body/50 mb-2">Nice to have</div>
                  <ul className="space-y-1">
                    {soft.map((f) => (
                      <li key={f.key} className="text-[13px] text-body/60">{f.label}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* The approver's channel. Never sent to the customer. */}
              {!!flags.length && (
                <div className="px-4 py-3 border-t border-krapf-border bg-krapf-gold-tint/60">
                  <div className="flex items-center gap-2 mb-2">
                    <IconWarning size={14} />
                    <span className="text-sm font-semibold text-body">For the reviewer</span>
                    <span className="text-[11px] text-body/50">not sent to the customer</span>
                  </div>
                  <ul className="space-y-1.5">
                    {flags.map((f, i) => (
                      <li key={`${f.kind}-${f.key}-${i}`} className="text-[13px] text-body/80">{f.message}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="px-4 py-4 border-t border-krapf-border">
                <Button
                  variant={ready ? 'primary' : 'outline'}
                  className="w-full"
                  disabled={!ready}
                  onClick={() => setSheetOpen(true)}
                >
                  {ready ? 'Open trip sheet' : 'Trip sheet — not ready yet'}
                </Button>
              </div>
            </>
          )}
        </aside>
      </div>

      {sheetOpen && (
        <TripSheet
          values={values}
          prefill={prefill}
          onClose={() => setSheetOpen(false)}
          onSendForPricing={() => {
            setSheetOpen(false);
            setNotice('Sent to pricing. (Demo: pricing is not wired up — the intake stops here.)');
          }}
        />
      )}
    </div>
  );
}

/** A realistic INCOMPLETE inquiry — it deliberately omits phone, depart time and the destination city. */
function sampleInquiry() {
  const year = new Date().getFullYear() + 1;
  return `Hi,

We need a coach for our team's away game at Lakemont Ice Arena on Saturday, November 21, ${year}.
Leaving from Ridgefield Rink. About 30 people plus hockey gear.

Thanks,
Ryan Mercer
Assistant Coach, Ridgefield College`;
}

/**
 * Write an inquiry the way a customer would and drop it in the inbox. Shared by the rail's
 * "New inquiry" button and the controls at the bottom of the reading pane, so the two can never
 * drift apart.
 */
function NewInquiryComposer({ onDelivered, autoFocus = false }) {
  const [from, setFrom] = useState('ryan.mercer@example.edu');
  const [subject, setSubject] = useState('Coach needed for our away game');
  const [body, setBody] = useState('');
  const h = harness();

  const deliver = () => {
    const conversationId = h.injectInquiry({
      fromAddress: from, fromName: from.split('@')[0], subject, body,
    });
    setBody('');
    onDelivered(conversationId);
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input value={from} onChange={(e) => setFrom(e.target.value)} placeholder="from address"
               className="flex-1 min-w-0 text-[13px] border border-krapf-border rounded-btn px-2 py-1.5" />
        <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="subject"
               className="flex-1 min-w-0 text-[13px] border border-krapf-border rounded-btn px-2 py-1.5" />
      </div>
      <textarea
        value={body} onChange={(e) => setBody(e.target.value)} rows={7} autoFocus={autoFocus}
        placeholder="Write the inquiry as a customer would — leave a few details out so the agent has something to ask for."
        className="w-full text-[13px] font-mono border border-krapf-border rounded-btn p-2"
      />
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={() => setBody(sampleInquiry())}>Use a sample</Button>
        <Button size="sm" disabled={!body.trim()} onClick={deliver}>Deliver to inbox</Button>
      </div>
    </div>
  );
}

/**
 * The rail's "New inquiry" button and its compose dialog. Mock mailbox only.
 *
 * A dialog rather than a panel in the rail: the rail is 288px wide and scrolls, so an inline composer
 * would be both cramped and clipped.
 */
function NewInquiryButton({ onDelivered }) {
  const [open, setOpen] = useState(false);
  if (!isMock()) return null;
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>+ New inquiry</Button>
      {open && (
        <div
          className="fixed inset-0 z-30 bg-primary/30 flex items-start justify-center p-6 overflow-y-auto"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-white rounded-card shadow-lg w-full max-w-xl mt-16 p-5 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2">
              <IconSparkles size={16} />
              <span className="text-sm font-semibold text-body">Send an inquiry as a customer</span>
              <DemoBadge title="Simulated mail. The intake agent behind it is real." />
              <button type="button" onClick={() => setOpen(false)}
                      className="ml-auto text-body/50 hover:text-body" aria-label="Close">
                <IconX size={16} />
              </button>
            </div>
            <p className="text-xs text-body/60">
              This drops a new email into the inbox exactly as if a customer had sent it. Leave a few
              details out so the agent has something to ask for.
            </p>
            <NewInquiryComposer autoFocus onDelivered={(id) => { setOpen(false); onDelivered(id); }} />
          </div>
        </div>
      )}
    </>
  );
}

/**
 * The test harness, shown only against the mock mailbox.
 *
 * This is what makes the workflow provable without Outlook: drop a new inquiry into the inbox, then
 * answer as the customer and watch the thread's poll pick it up and run the next turn. Everything it
 * triggers downstream — extraction, gap detection, the composed reply — is the real agent.
 */
function MockHarness({ selected, onChanged }) {
  const [open, setOpen] = useState(false);
  const h = harness();

  return (
    <div className="m-6 rounded-card border border-dashed border-krapf-gold/50 bg-krapf-gold-tint/60">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-left"
      >
        <IconSparkles size={15} />
        <span className="text-sm font-semibold text-body">Test mailbox controls</span>
        <DemoBadge title="Simulated mail. The intake agent behind it is real." />
        <span className="ml-auto text-xs text-body/50">{open ? 'hide' : 'show'}</span>
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-4">
          <p className="text-xs text-body/60">
            Only the mail is simulated. Anything you send here runs through the same flow, the same
            extraction and the same reply composer that Outlook would.
          </p>

          <div className="space-y-2">
            <div className="text-xs uppercase tracking-wide text-body/50">1 · Send a new inquiry</div>
            <NewInquiryComposer onDelivered={() => onChanged()} />
          </div>

          <div className="space-y-2 border-t border-krapf-gold/30 pt-3">
            <div className="text-xs uppercase tracking-wide text-body/50">
              2 · Reply as the customer{selected ? '' : ' — open a thread first'}
            </div>
            <CustomerReply
              disabled={!selected}
              onSend={(text) => { h.replyAsCustomer(selected.conversationId, text); onChanged(); }}
            />
            <p className="text-xs text-body/60">
              The thread polls every 20 seconds, so the agent picks this up on its own — same as a real
              reply landing in Outlook.
            </p>
          </div>

          <div className="border-t border-krapf-gold/30 pt-3">
            <Button size="sm" variant="ghost"
                    onClick={() => { h.resetMailbox(); window.localStorage.removeItem('krapf.threadSessions.v1'); onChanged(); }}>
              Reset the test mailbox
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function CustomerReply({ disabled, onSend }) {
  const [text, setText] = useState('');
  return (
    <>
      <textarea
        value={text} onChange={(e) => setText(e.target.value)} rows={4} disabled={disabled}
        placeholder="Answer some of the questions — and try changing something you already said, to see it flagged as changed."
        className="w-full text-[13px] font-mono border border-krapf-border rounded-btn p-2 disabled:bg-border/20"
      />
      <Button size="sm" disabled={disabled || !text.trim()} onClick={() => { onSend(text); setText(''); }}>
        Send as customer
      </Button>
    </>
  );
}

function TripSheet({ values, prefill, onClose, onSendForPricing }) {
  const rows = values.returnJourney === 'round-trip'
    ? SHEET_ROWS
    : SHEET_ROWS.filter((r) => !RETURN_KEYS.has(r.key));
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-6 z-50" onClick={onClose}>
      <div className="bg-white rounded-card shadow-pop max-w-3xl w-full max-h-[85vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between px-6 py-4 border-b border-krapf-border">
          <div>
            <div className="text-xs uppercase tracking-wide text-body/50">
              Trip sheet · {values.returnJourney === 'round-trip' ? 'Round trip' : 'One-way'}
            </div>
            <h2 className="text-lg font-semibold text-body">
              {values.groupName || prefill.groupName?.value || 'Charter trip'}
              {values.dropoffCity ? ` to ${values.dropoffCity}` : ''}
            </h2>
            <div className="text-sm text-body/60">
              {[values.departureDate, values.departureTime].filter(Boolean).join(' · ')}
            </div>
          </div>
          <button onClick={onClose} className="text-body/40 hover:text-body"><IconX size={18} /></button>
        </div>

        <dl className="px-6 py-4 grid grid-cols-2 gap-x-8 gap-y-3">
          {rows.map(({ key, label }) => {
            const v = values[key] || (prefill[key] && prefill[key].value);
            if (!v) return null;
            return (
              <div key={key}>
                <dt className="text-[11px] uppercase tracking-wide text-body/50">{label}</dt>
                <dd className="text-sm text-body">{v}</dd>
              </div>
            );
          })}
        </dl>

        <div className="px-6 py-4 border-t border-krapf-border flex items-center gap-3">
          <IconLock size={14} />
          <span className="text-xs text-body/60">
            Pricing is set by your team. The intake never sees your rates.
          </span>
          <Button className="ml-auto" onClick={onSendForPricing}>Send for pricing</Button>
        </div>
      </div>
    </div>
  );
}

/** Shown instead of a broken inbox when the environment is not wired up yet. */
function SetupNeeded({ clientId, flowKey }) {
  const Row = ({ ok, name, what }) => (
    <li className="flex items-start gap-2 text-sm">
      <span className={cx('mt-0.5', ok ? 'text-success' : 'text-danger')}>
        {ok ? <IconCircleCheck size={15} /> : <IconWarning size={15} />}
      </span>
      <span><code className="text-[12px] bg-border/40 px-1 rounded">{name}</code> — {what}</span>
    </li>
  );
  return (
    <div className="h-screen flex items-center justify-center bg-krapf-surface p-6">
      <div className="bg-white border border-krapf-border rounded-card p-6 max-w-lg">
        <h1 className="text-lg font-semibold text-krapf-navy mb-1">Krapf Charter Mail needs two settings</h1>
        <p className="text-sm text-body/70 mb-4">
          Both are build-time environment variables. Create React App bakes them at start, so restart
          <code className="text-[12px] bg-border/40 px-1 rounded mx-1">npm start</code> after adding them.
        </p>
        <ul className="space-y-2">
          <Row ok={!!clientId} name="REACT_APP_KRAPF_GRAPH_CLIENT_ID"
               what="the Azure app registration (public SPA client) used to sign in to Outlook." />
          <Row ok={!!flowKey} name="REACT_APP_KRAPF_ECAP_FLOW_KEY"
               what="the krapf-charter-intake flow's public key, printed by templates/krapf/import.sh." />
        </ul>
      </div>
    </div>
  );
}
